import type { SignOptions } from 'jsonwebtoken'

export const JWT_SECRET = process.env.JWT_SECRET!

const checkJWTSecretLength = () => {
  if (!JWT_SECRET || JWT_SECRET?.length < 64) {
    throw new Error('JWT_SECRET_TO_SHORT')
  }
}

type JWTReturnType = string | { [key: string]: any }

// Lazy Load JWT-Library,
// so that NextJS does not polyfill it on the client with `crypto-browserify`
const getJwt = async () => {
  const jwt = await import('jsonwebtoken')
  return jwt
}

export const verifyJWT = async <T extends JWTReturnType = JWTReturnType>(
  token: string
): Promise<T> => {
  checkJWTSecretLength()
  const jwt = await getJwt()

  return jwt.verify(token, JWT_SECRET, {
    algorithms: ['HS256'],
  }) as any
}

export type CreateJWTOptions = {
  /** expressed in seconds or a string describing a time span [zeit/ms](https://github.com/zeit/ms.js).  Eg: 60, "2 days", "10h", "7d" */
  expiresIn?: string | number | undefined
}
export const createJWT = async (
  jwtClaims: string | object | Buffer,
  { expiresIn }: CreateJWTOptions = {}
) => {
  checkJWTSecretLength()

  const signOptions: SignOptions = {
    algorithm: 'HS256',
  }

  if (expiresIn) {
    signOptions.expiresIn = expiresIn
  }
  const jwt = await getJwt()
  return jwt.sign(jwtClaims, JWT_SECRET, signOptions)
}

export const getJWTPayloadWithoutVerification = async <
  T extends JWTReturnType = JWTReturnType
>(
  token: string
): Promise<T> => {
  const jwt = await getJwt()
  return jwt.decode(token) as any
}

// TODO: is this a good way to do it?
export const getJWTPayloadWithoutVerificationBrowser = (token: string) => {
  // FROM: https://stackoverflow.com/a/38552302
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}
